import type { ReactNode } from "react";
import type { MediaItem, Region, RoomRegionState } from "@gooner/shared";
import { DrivenRegion } from "./DrivenRegion";
import { RegionPlayer } from "./RegionPlayer";
import { RegionFapOverlay } from "./RegionFapOverlay";
import { SyncedRegion } from "./SyncedRegion";
import type { FapBus, RegionRegistry } from "./types";
import type { SyncBus } from "./syncBus";

/** A Goon Room member's tile plays what the HOST's region plays — see DrivenRegion. */
export interface DrivenBy {
  bus: SyncBus<RoomRegionState>;
  /** This browser's offset from the server's clock (useServerClockOffset). */
  serverOffset: number;
}

/**
 * ONE REGION ON THE GLASS — the box at the region's rect, the selection ring, the player
 * (or the synced follower), and the fap crown. Every stage that puts regions on a screen
 * renders this: the default stage in DisplayApp (a view's authored regions) and the
 * Infinity stage (a dealt page of them). It exists so those two cannot drift into two
 * slightly different tiles — the ring's z-index, the follower rule, the crown's
 * getCurrent — while each keeps its own GESTURES.
 *
 * The pointer handlers are the host's, passed through untouched. The screen's tap
 * contract (one tap selects, a double opens, mouseleave means nothing under a finger)
 * closes over the screen's state and belongs there; the Infinity stage wants a simpler
 * one (a tap toggles sound). A tile that owned the gesture would have to know every
 * host's mind, so it owns none.
 *
 * `children` is the host's chrome — the hover controls, a wall's price plate — mounted
 * over the player in the tile's own stacking context, exactly where they sat before this
 * component existed. See RegionFapOverlay for the corner/z-index contract they share.
 */
export function RegionTile({
  region,
  leader,
  driven,
  registry,
  syncBus,
  paused,
  muted,
  volume,
  onAutoMuted,
  onView,
  vetted,
  selected,
  ringTint,
  signedIn,
  fapBus,
  fapSurface,
  onFapped,
  onMouseEnter,
  onMouseLeave,
  onPointerDown,
  onClick,
  children,
}: {
  region: Region;
  /**
   * The region this one follows, or null for a feed of its own. A follower plays its
   * leader's clip and runs no feed — no clip selection, no buffer, no advance timer, and
   * no `onView`: a synced pair is ONE clip being watched, and counting both would inflate
   * every view and fap number by however many followers a wall happened to have. The
   * host resolves it (syncLeaderOf over the authored regions) because only the host
   * holds the whole view.
   */
  leader: Region | null;
  /** Set on a room MEMBER's leaderless tiles: the clip comes from the host over the
   *  socket, never from a feed of this tile's own. A follower still follows its local
   *  leader (which is itself driven, and publishes to `syncBus` as a leader would). */
  driven?: DrivenBy;
  registry: RegionRegistry;
  syncBus: SyncBus;
  paused: boolean;
  /** The verdict, already the AND of every owner of sound — see RegionPlayer.muted. */
  muted: boolean;
  volume: number;
  onAutoMuted: () => void;
  onView: (item: MediaItem) => void;
  /** The region's own clips are already server-filtered — see RegionPlayer.vetted. */
  vetted?: boolean;
  /** Whether the ring is lit. What lights it is the host's business: hover, a tap, a
   *  link-menu peek, membership of a hovered sync group. */
  selected: boolean;
  /** A sync group's tint for the ring, so the group reads as one thing. */
  ringTint?: string;
  signedIn: boolean;
  fapBus: FapBus;
  /** Where a fap here is counted (fap_saved.surface); the screen by default. */
  fapSurface?: "screen" | "infinity";
  /** A fap happened on this tile, by the person here — a room tells the others. */
  onFapped?: (regionId: string) => void;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  onPointerDown?: React.PointerEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
}) {
  return (
    <div
      className="absolute"
      style={{
        left: `${region.x * 100}%`,
        top: `${region.y * 100}%`,
        width: `${region.w * 100}%`,
        height: `${region.h * 100}%`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDown={onPointerDown}
      onClick={onClick}
    >
      {/* The selection ring, in the accent. It advertises that the region is one big
          target — without it the tile reads as plain video rather than something a
          press does anything to. Ungated by any idle clock: hover IS the activity.
          Inset so it reads as the tile's own edge, and pointer-events-none so it never
          eats the click it advertises. The inline custom property outruns the class,
          which is how a synced tile rings in its group's tint. */}
      {selected ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 rounded-sm ring-2 ring-inset ring-[var(--color-accent)]"
          style={ringTint ? ({ "--tw-ring-color": ringTint } as React.CSSProperties) : undefined}
        />
      ) : null}
      {leader ? (
        <SyncedRegion region={region} leaderId={leader.id} bus={syncBus} paused={paused} />
      ) : driven ? (
        <DrivenRegion
          region={region}
          bus={driven.bus}
          serverOffset={driven.serverOffset}
          registry={registry}
          syncBus={syncBus}
          paused={paused}
          muted={muted}
          volume={volume}
          onView={onView}
        />
      ) : (
        <RegionPlayer
          region={region}
          registry={registry}
          paused={paused}
          muted={muted}
          volume={volume}
          onAutoMuted={onAutoMuted}
          onView={onView}
          syncBus={syncBus}
          vetted={vetted}
        />
      )}
      {/* Always mounted, never unmounted on idle: the fap is the one control you reach
          for WHILE watching, without warning, on the clip in front of you — making
          somebody wake the UI first is asking them to take a step to reach the thing the
          step was hiding. */}
      <RegionFapOverlay
        regionId={region.id}
        // A follower faps its LEADER's clip — the only clip it is showing. Read through
        // the registry at press time, never at render: the registry is a ref, so an
        // advance re-renders nothing here, and a clip read now would be frozen at
        // whatever happened to be playing on the last render.
        getCurrent={() => registry.get((leader ?? region).id)?.current() ?? null}
        signedIn={signedIn}
        fapBus={fapBus}
        surface={fapSurface}
        onFapped={onFapped}
      />
      {children}
    </div>
  );
}
